import React, { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { API } from "../config/api";
import { Sentry } from "../lib/sentry";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("globaleyes_token"));
  const [loading, setLoading] = useState(true);

  // Tag every Sentry error with the current user so we can filter
  // "errors hit by Producer+ users" or "errors hit by @ehouser" from
  // the dashboard. Cleared on logout so anonymous errors stay anon.
  useEffect(() => {
    try {
      if (user) {
        Sentry.setUser({
          id: user.id,
          username: user.username,
          // Don't ship email — privacy + reduces PII exposure on
          // the Sentry side.
        });
      } else {
        Sentry.setUser(null);
      }
    } catch { /* Sentry not initialized in dev — ignore */ }
  }, [user]);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const res = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(res.data);
        } catch (err) {
          localStorage.removeItem("globaleyes_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("globaleyes_token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  // Passwordless sign-in: exchange a magic-link token for a JWT.
  // Used by /magic-login/:token after the user taps the email CTA.
  const loginWithMagicToken = async (magicToken) => {
    const res = await axios.post(`${API}/auth/magic-link/redeem`, { token: magicToken });
    localStorage.setItem("globaleyes_token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  // Public wrapper for "send me a magic link" from the auth form.
  // Returns the server's generic OK message regardless of whether the
  // email is registered — anti-enumeration.
  const requestMagicLink = async (email) => {
    const res = await axios.post(`${API}/auth/magic-link/request`, {
      email: (email || "").trim().toLowerCase(),
      // Pass the active host so the magic-link URL the server bakes into
      // the email lands the user back on whichever environment they came
      // from (prod / preview / iOS Capacitor webview).
      origin_url: typeof window !== "undefined" ? window.location.origin : undefined,
    });
    return res.data;
  };

  const register = async (username, email, password, extras = {}) => {
    // `extras` carries optional home_city + lat/lng so the
    // auto_city_commissions seeder fires on signup. Backend treats
    // those fields as optional — if a legacy signup form doesn't
    // include them, the seeder no-ops silently rather than 500ing.
    //
    // Profile-as-referral-link: read `ge.x_inv` from localStorage —
    // the XAttributionBeacon captured it when the visitor landed on
    // /{inviter}?inv={inviter}. Forwarded to backend which validates
    // and stamps `referred_by_username` on the new user. Bogus / self-
    // refers are silently dropped server-side.
    let referrerUsername = null;
    try { referrerUsername = localStorage.getItem("ge.x_inv") || null; }
    catch { /* private mode */ }
    const res = await axios.post(`${API}/auth/register`, {
      username, email, password,
      home_city: extras.home_city,
      home_city_country: extras.home_city_country,
      home_city_lat: extras.home_city_lat,
      home_city_lng: extras.home_city_lng,
      referrer_username: referrerUsername || undefined,
      captcha_token: extras.captcha_token || undefined,
    });
    localStorage.setItem("globaleyes_token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    // First-touch nudge to add the site to the user's home screen.
    // We delay 2.5s so the registration success toast lands first, then
    // the install-prompt dialog opens. Skipped if the user already
    // dismissed/installed (the dialog itself respects the localStorage
    // gate, so we can fire blindly).
    setTimeout(() => {
      try {
        const dismissed = localStorage.getItem("globaleyes_install_dismissed");
        const isStandalone =
          window.matchMedia("(display-mode: standalone)").matches ||
          window.navigator.standalone === true;
        if (!dismissed && !isStandalone) {
          window.dispatchEvent(new CustomEvent("globaleyes:open-install-prompt"));
        }
      } catch { /* ignore */ }
    }, 2500);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("globaleyes_token");
    setToken(null);
    setUser(null);
  };

  const updateCredits = (newBalance) => {
    if (user) {
      setUser({ ...user, credits: newBalance });
      // Dispatch custom event so Header can trigger the blink animation
      window.dispatchEvent(new CustomEvent('credits-deducted'));
    }
  };

  const refreshUser = async () => {
    if (token) {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    }
  };

  // QR-handoff sign-in: the desktop poller hands us the JWT + user shape
  // the moment the phone confirms. Same end-state as `login` but no
  // password ever entered on this device.
  const setSessionFromQR = (payload) => {
    if (!payload?.access_token) return;
    localStorage.setItem("globaleyes_token", payload.access_token);
    setToken(payload.access_token);
    if (payload.user) setUser(payload.user);
  };

  // X sign-in (Iter-56): same shape as the QR handoff — accept a
  // server-side payload from /api/x/signin/callback and adopt it as
  // our auth state. Distinct method name keeps intent obvious at call
  // sites + makes Sentry breadcrumbs easier to trace.
  const setSessionFromX = (payload) => {
    if (!payload?.access_token) return;
    localStorage.setItem("globaleyes_token", payload.access_token);
    setToken(payload.access_token);
    if (payload.user) setUser(payload.user);
  };

  // SMS sign-in (Phase 4): start the OTP send. Server returns 200 even
  // when Twilio is mid-pause so we don't leak quota state to the UI.
  const requestSMSCode = async (phoneE164) => {
    const res = await axios.post(`${API}/auth/sms/send`, { phone_e164: phoneE164 });
    return res.data;
  };

  // SMS sign-in: redeem the 6-digit OTP for a JWT + user. Mirrors
  // loginWithMagicToken — single call, sets state, returns the payload.
  const verifySMSCode = async (phoneE164, code) => {
    const res = await axios.post(`${API}/auth/sms/verify`, { phone_e164: phoneE164, code });
    localStorage.setItem("globaleyes_token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateCredits, refreshUser, requestMagicLink, loginWithMagicToken, setSessionFromQR, setSessionFromX, requestSMSCode, verifySMSCode }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
